import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { OrderAssignment } from '@klareospace/pharos-lib-types';
import { getFullName } from '@klareospace/pharos-lib-utils';
import { InlineLabel, StackedCell, Tooltip, TooltipContent, TooltipTrigger } from '@klareospace/pharos-lib-web-utils';

import { CarrierAvatar } from '&/features/settings/carriers/components/carrier-avatar';

import { CountPill } from './count-pill';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CarrierDriverCellProps {
  assignedTo?: OrderAssignment[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CarrierDriverCell({ assignedTo }: CarrierDriverCellProps): React.JSX.Element {
  const { t } = useTranslation('orders');

  if (!assignedTo || assignedTo.length === 0) {
    return <span className="text-sm text-muted-foreground">{t('table.unassigned')}</span>;
  }

  const [first, ...rest] = assignedTo;

  const driverName =
    first.driver !== undefined ? getFullName(first.driver.firstName, first.driver.lastName) : undefined;

  return (
    <div className="flex items-center gap-1.5">
      <StackedCell
        primary={
          <InlineLabel
            prefix={<CarrierAvatar className="font-semibold w-5 h-5 text-[9px]" name={first.carrier.name} />}
            size="sm"
          >
            {first.carrier.name}
          </InlineLabel>
        }
        secondary={driverName}
      />
      {rest.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <CountPill className="cursor-default">{`+${rest.length}`}</CountPill>
          </TooltipTrigger>
          <TooltipContent align="start" className="max-w-56 p-3">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('table.otherLinkedTasks')}
            </p>
            <ul className="flex flex-col gap-1">
              {rest.map((assignment) => (
                <li key={assignment.carrier.id} className="text-xs">
                  {assignment.carrier.name}
                  {assignment.driver && (
                    <span className="text-muted-foreground">
                      {' · '}
                      {getFullName(assignment.driver.firstName, assignment.driver.lastName)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
